import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

export default function TermsOfService() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - BridgeOnce</title>
        <meta 
          name="description" 
          content="BridgeOnce's terms of service outline the rules and guidelines for using our services."
        />
      </Helmet>

      <main className="pt-24 bg-background">
        {/* Using same breadcrumb structure as Privacy Policy */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="text-sm text-text/60">
            <a href="/" className="hover:text-primary">Home</a>
            <span className="mx-2">/</span>
            <span className="text-text">Terms of Service</span>
          </nav>
        </div>

        <motion.div 
          className="max-w-4xl mx-auto px-6 py-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="text-center mb-16"
            variants={itemVariants}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-text/80">
              Last Updated: January 18, 2025
            </p>
          </motion.div>

          <motion.div 
            className="prose prose-lg max-w-none"
            variants={itemVariants}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-text/80 mb-6">
                If you have any questions, concerns, or are unclear about any part of the Terms of Service, 
                please contact us at privacy@bridgeonce.com.
              </p>

              {/* Table of Contents - similar structure to Privacy Policy */}
              <div className="bg-background rounded-xl p-6 mb-8">
                <h2 className="text-xl font-semibold text-primary mb-4">Table of Contents</h2>
                <ul className="space-y-2">
                  {[
                    'Agreement Between User and BridgeOnce',
                    'Modification of These Terms of Use',
                    'Service Description and Use Limitations',
                    'Privacy and Security',
                    'Device Usage Terms',
                    'No Unlawful or Prohibited Use',
                    'Software, Hardware, and Content',
                    'Event Organizer Terms',
                    'Liability Disclaimer',
                    'Payments and Refunds',
                    'Termination / Access Restrictions',
                    'Copyright and Trademark Notices'
                  ].map((item) => (
                    <li key={item}>
                      <a 
                        href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                        className="text-secondary hover:text-primary transition-colors duration-200"
                      >
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Terms Sections */}
              {[
                {
                  id: 'agreement-between-user-and-bridgeonce',
                  title: 'Agreement Between User and BridgeOnce',
                  content: (
                    <p>
                      The BridgeOnce Services, including our website, networking devices, and related services, are offered to you conditioned on your acceptance without modification of the terms, conditions, and notices contained herein. Your use of any BridgeOnce Services constitutes your agreement to all such terms, conditions, and notices. These Terms of Use apply to all users of the Services, whether accessing them as event organizers, attendees, or website visitors.
                    </p>
                  )
                },
                {
                  id: 'modification-of-these-terms-of-use',
                  title: 'Modification of These Terms of Use',
                  content: (
                    <p>
                      BridgeOnce reserves the right to change the terms, conditions, and notices under which the BridgeOnce Services are offered, including but not limited to the charges associated with the use of the BridgeOnce Services. You are responsible for regularly reviewing these terms and conditions and additional terms posted on our website. Your continued use of the BridgeOnce Services constitutes your agreement to all such terms, conditions, and notices.
                    </p>
                  )
                },
                {
                  id: 'service-description-and-use-limitations',
                  title: 'Service Description and Use Limitations',
                  content: (
                    <>
                      <p>
                        The BridgeOnce Services encompass our professional networking platform, which includes physical NFC devices, digital profile management, and related services. Unless otherwise specified, the BridgeOnce Services are for professional networking use within the context of events and business interactions. You may not modify, copy, distribute, transmit, display, perform, reproduce, publish, license, create derivative works from, transfer, or sell any information, software, products, or services obtained from BridgeOnce without our explicit written permission.
                      </p>
                      <p className="mt-4">
                        Our Services are designed to facilitate professional connections through the secure sharing of business profiles and contact information. The core functionality includes profile creation, management, and sharing through NFC-enabled devices, as well as associated digital services for managing these interactions. While we strive to maintain the highest standards of service, we cannot guarantee uninterrupted access or specific networking outcomes.
                      </p>
                    </>
                  )
                },
                {
                  id: 'privacy-and-security',
                  title: 'Privacy and Security',
                  content: (
                    <p>
                      Your use of BridgeOnce is subject to our Privacy Policy. You agree that you have read our Privacy Policy, and it is reasonable and acceptable to you. Your acceptance of these Terms is also your consent to the information practices in our Privacy Policy. We implement industry-standard security measures to protect your information, but no method of electronic storage or transmission is 100% secure.
                    </p>
                  )
                },
                {
                  id: 'device-usage-terms',
                  title: 'Device Usage Terms',
                  content: (
                    <>
                      <p>
                        The NFC devices provided through BridgeOnce are designed for professional networking purposes and remain the intellectual property of BridgeOnce. While the physical devices may be distributed to event attendees, the underlying technology, firmware, and associated rights remain with BridgeOnce. You agree to use the devices as intended and in accordance with any provided instructions or guidelines.
                      </p>
                      <p className="mt-4">
                        The devices are provided with a limited warranty against manufacturing defects, subject to proper use and care. This warranty does not cover damage from misuse, attempted modification, or circumstances beyond normal operating conditions. Devices are configured specifically for each event and may not be transferred or repurposed without explicit authorization from BridgeOnce.
                      </p>
                    </>
                  )
                },
                {
                  id: 'no-unlawful-or-prohibited-use',
                  title: 'No Unlawful or Prohibited Use',
                  content: (
                    <p>
                      As a condition of your use of the BridgeOnce Services, you will not use the Services for any purpose that is unlawful or prohibited by these terms, conditions, and notices. You may not use the BridgeOnce Services in any manner which could damage, disable, overburden, or impair any BridgeOnce Service or interfere with any other party's use and enjoyment of the Services. You may not attempt to gain unauthorized access to any BridgeOnce Services, other accounts, computer systems or networks connected to any BridgeOnce Service, through hacking, password mining or any other means. You may not obtain or attempt to obtain any materials or information through any means not intentionally made available through the BridgeOnce Services.
                    </p>
                  )
                },
                {
                  id: 'software-hardware-and-content',
                  title: 'Software, Hardware, and Content',
                  content: (
                    <>
                      <p>
                        All content, software, and hardware designs made available through BridgeOnce Services, excluding content provided by users through normal service use, is owned by and is the copyrighted work of BridgeOnce and/or its suppliers and is protected by copyright laws and international treaty provisions. Your use of any software components is governed by the terms of the end user license agreement, if any, which accompanies or is included with the software. You may not install or use any software that is accompanied by or includes a License Agreement unless you first agree to the License Agreement terms.
                      </p>
                      <p className="mt-4">
                        For any software not accompanied by a specific license agreement, BridgeOnce hereby grants to you a revocable personal, non-transferable license to use the software solely for accessing and using the BridgeOnce Services in accordance with these Terms of Use. Any reproduction or redistribution of the content, software, or hardware designs is expressly prohibited by law, and may result in severe civil and criminal penalties. Violators will be prosecuted to the maximum extent possible. You acknowledge that the software, hardware designs, and any accompanying documentation and/or technical information are subject to applicable export control laws and regulations.
                      </p>
                    </>
                  )
                },
                {
                  id: 'event-organizer-terms',
                  title: 'Event Organizer Terms',
                  content: (
                    <>
                      <p>
                        Event organizers utilizing BridgeOnce Services agree to additional responsibilities and terms specific to their role. Event organizers must provide accurate and complete attendee information necessary for profile creation and device configuration. This information must be provided in accordance with applicable data protection laws and with appropriate consent from attendees. Event organizers are responsible for the proper distribution and collection of devices at their events, as well as ensuring attendees are informed of proper device usage.
                      </p>
                      <p className="mt-4">
                        Event organizers acknowledge that device customization, including branding and specific features, must be requested within specified timeframes before events. Late modifications may not be possible or may incur additional charges. Event organizers are responsible for maintaining the confidentiality of any administrative access credentials provided by BridgeOnce and must promptly report any unauthorized access or security concerns.
                      </p>
                    </>
                  )
                },
                {
                  id: 'liability-disclaimer',
                  title: 'Liability Disclaimer',
                  content: (
                    <>
                      <p>
                        The information, software, hardware, and services included in or available through BridgeOnce Services may include inaccuracies or typographical errors. Changes are periodically made to the BridgeOnce Services and to the information therein. BridgeOnce and/or its respective suppliers may make improvements and/or changes in the BridgeOnce Services at any time without notice.
                      </p>
                      <p className="mt-4">
                        BridgeOnce and/or its respective suppliers make no representations about the suitability, reliability, availability, timeliness, lack of viruses or other harmful components and accuracy of the information, software, hardware, services and related graphics contained within the BridgeOnce Services for any purpose. All such information, software, hardware, services and related graphics are provided "as is" without warranty of any kind.
                      </p>
                      <p className="mt-4">
                        In no event shall BridgeOnce and/or its suppliers be liable for any direct, indirect, punitive, incidental, special, consequential damages or any damages whatsoever including, without limitation, damages for loss of use, data or profits, arising out of or in any way connected with the use or performance of the BridgeOnce Services.
                      </p>
                    </>
                  )
                },
                {
                  id: 'payments-and-refunds',
                  title: 'Payments and Refunds',
                  content: (
                    <>
                      <p>
                        BridgeOnce reserves the right to charge for the use of our Services, which may include per-device fees, customization fees, subscription fees, or other forms of payment, at its sole discretion. Payment terms, including the type and amount of fees, are subject to change at any time. Changes may take effect immediately upon being posted, and BridgeOnce is not obligated to notify you of any fee adjustments.
                      </p>
                      <p className="mt-4">
                        For physical devices and related hardware, payment is required in full before production begins. Customization requests and specific branding requirements must be submitted with adequate lead time as specified in the order process. Rush orders, if available, may incur additional fees.
                      </p>
                      <p className="mt-4">
                        Refund requests for device orders must be made before production begins. Once production has commenced, refunds may be limited or unavailable due to the custom nature of the devices. For digital services, refund requests must be made within thirty (30) days from the payment date.
                      </p>
                    </>
                  )
                },
                {
                  id: 'termination-access-restrictions',
                  title: 'Termination / Access Restrictions',
                  content: (
                    <>
                      <p>
                        BridgeOnce reserves the right, in its sole discretion, to terminate your access to any or all BridgeOnce Services or any portion thereof at any time, without notice. This includes the right to deactivate or recall physical devices if necessary. BridgeOnce may terminate the Services at any time, without notice.
                      </p>
                      <p className="mt-4">
                        For digital services, BridgeOnce may terminate or suspend your access for inactivity, which is defined as failing to log into a particular service for an extended period of time, as determined by BridgeOnce. Upon termination of the BridgeOnce Services, your right to use the Services immediately ceases.
                      </p>
                    </>
                  )
                },
                {
                  id: 'copyright-and-trademark-notices',
                  title: 'Copyright and Trademark Notices',
                  content: (
                    <>
                      <p>
                        All contents of the BridgeOnce Services are Copyright © 2023-2025 BridgeOnce and/or its suppliers. All rights reserved.
                      </p>
                      <p className="mt-4">
                        Any rights not expressly granted herein are reserved.
                      </p>
                    </>
                  )
                }
              ].map((section) => (
                <div 
                  key={section.id}
                  id={section.id}
                  className="mb-12"
                >
                  <h2 className="text-2xl font-bold text-primary mb-4">
                    {section.title}
                  </h2>
                  <div className="text-text/80 space-y-4">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </main>
    </>
  );
} 